import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('srm_token'));
  const [loading, setLoading] = useState(true);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('srm_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('srm_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;
    
    localStorage.setItem('srm_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setRequiresMFA(data.requires_mfa);
    setMfaSetupRequired(data.mfa_setup_required);
    
    return data;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const data = response.data;
    
    localStorage.setItem('srm_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setMfaSetupRequired(data.mfa_setup_required);
    
    return data;
  };

  const setupMFA = async (mfaType, phoneNumber = null) => {
    const response = await api.post('/auth/mfa/setup', {
      mfa_type: mfaType,
      phone_number: phoneNumber
    });
    return response.data;
  };

  const verifyMFA = async (code, mfaType = null) => {
    const response = await api.post('/auth/mfa/verify', { code, mfa_type: mfaType });
    const data = response.data;
    
    localStorage.setItem('srm_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setRequiresMFA(false);
    setMfaSetupRequired(false);
    
    return data;
  };

  const resendMFA = async () => {
    const response = await api.post('/auth/mfa/resend');
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('srm_token');
    setToken(null);
    setUser(null);
    setRequiresMFA(false);
    setMfaSetupRequired(false);
  };

  const refreshUser = async () => {
    const response = await api.get('/auth/me');
    setUser(response.data);
    setAvatarVersion(Date.now());
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    requiresMFA,
    mfaSetupRequired,
    avatarVersion,
    refreshUser,
    permissions: user?.permissions || {},
    login,
    register,
    setupMFA,
    verifyMFA,
    resendMFA,
    logout,
    api,
    setRequiresMFA,
    setMfaSetupRequired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
